/**
 * Short-Term Disability (STD) skeleton
 *
 * Provides the default structure for STD coverage with empty benefit plans.
 * Returns grid format (flat fields) for use in the PlanDesigner grid.
 */

import {
  BaseBenefitPlanPolicyPeriod,
  BenefitAmountTypeEnum,
  MaximumAmountTypeEnum,
  EliminationPeriodTypeEnum,
  RfpStdBenefitDurationTypeEnum,
  PreExistingConditionTypeEnum,
} from "@platform/openapi-types";

import { RfpBenefitPlan } from "../../types/rfp.api.types";

import type { StdBenefitGrid } from "./types";

/**
 * STD benefit plan structure for skeleton
 */
export interface StdBenefitPlanSkeleton {
  identifier: string;
  name: string;
  numberOfLives: number;
  type: "notApplicable";
  policyPeriod: BaseBenefitPlanPolicyPeriod;
  fundingType: "fullyInsured";
  participationRequirements: string;
  rateGuarantee?: RfpBenefitPlan["rateGuarantee"];
  rateCap: string;
  optionName: "notApplicable";
  benefitClassAvailability: never[];
  rateSchedule: never[];
  benefits: StdBenefitGrid;
}

/**
 * STD coverage skeleton structure
 */
export interface StdCoverageSkeleton {
  type: "std";
  status: "quoted";
  notes: string;
  benefitPlans: StdBenefitPlanSkeleton[];
}

/**
 * Creates an empty STD benefit object in grid format
 * @returns Empty STD benefit with all fields set to default values
 */
export const getStdBenefitSkeleton = (): StdBenefitGrid => ({
  objectType: "std",

  // Benefit Amount - flat fields for grid
  benefitAmountType: BenefitAmountTypeEnum.increments,
  benefitAmountAmount: undefined,
  benefitAmountMinimum: undefined,

  // Maximum Amount - flat fields for grid
  maximumAmountType: MaximumAmountTypeEnum.dollarAmount,
  maximumAmountDollarAmount: undefined,
  maximumAmountPercent: undefined,

  // Definition of Disability - simple enum
  definitionOfDisability: undefined,

  // Definition of Earnings - flat fields for grid
  definitionOfEarningsType: "salary",
  definitionOfEarningsAverageMonths: undefined,

  // Employer Contribution - simple enum
  employerContribution: undefined,

  // Elimination Period - Accident - flat fields for grid
  eliminationPeriodAccidentType: EliminationPeriodTypeEnum.days,
  eliminationPeriodAccidentDays: undefined,

  // Elimination Period - Illness - flat fields for grid
  eliminationPeriodIllnessType: EliminationPeriodTypeEnum.days,
  eliminationPeriodIllnessDays: undefined,

  // Benefit Duration - flat fields for grid
  benefitDurationType: RfpStdBenefitDurationTypeEnum.weeks,
  benefitDurationAmount: undefined,

  // Pre-Existing Condition - flat fields for grid
  preExistingConditionType: PreExistingConditionTypeEnum.months,
  preExistingConditionMonthsDetail: undefined,

  // FICA Match - simple enum
  ficaMatch: undefined,

  // Billable Type - simple enum
  billableType: undefined,

  // Extended (non-v5) fields — CD-2068
  eligibilityHours: undefined,
  minimumBenefitType: undefined,
  minimumBenefitAmount: undefined,
  firstDayHospital: undefined,
  partialDisabilityFormula: undefined,
  partialDisability: undefined,
  earningsTest: undefined,
  portability: undefined,
  rehabilitativeIncentiveBenefit: undefined,
  recurrentDisability: undefined,
  w2Prep: undefined,
  claimsIntake: undefined,
  statutoryDiIntegration: undefined,
  pflIntegration: undefined,
});

/**
 * Gets the STD coverage skeleton (kept for backwards compatibility with skeleton registry)
 */
export const getStdSkeleton = (): StdCoverageSkeleton => ({
  type: "std" as const,
  status: "quoted" as const,
  notes: "",
  benefitPlans: [
    {
      identifier: "std-plan-1",
      name: "Short-Term Disability Plan",
      numberOfLives: 0,
      type: "notApplicable" as const,
      policyPeriod: BaseBenefitPlanPolicyPeriod.newBusiness,
      fundingType: "fullyInsured" as const,
      participationRequirements: "",
      rateGuarantee: undefined,
      rateCap: "",
      optionName: "notApplicable" as const,
      benefitClassAvailability: [],
      rateSchedule: [],
      benefits: getStdBenefitSkeleton(),
    },
  ],
});
